<template>
  <Teleport to="body">
    <Transition name="title-hover">
      <aside
        v-if="preview.hover.value && !selection"
        class="title-hover"
        :style="hoverStyle"
        role="dialog"
        :aria-label="`${hoverMedia.title} preview`"
        @mouseenter="preview.keepHover"
        @mouseleave="onHoverMouseLeave"
        @focusin="preview.keepHover"
        @focusout="onHoverFocusOut"
      >
        <TitlePreviewMedia :image="hoverMedia.backdrop || hoverMedia.poster || ''" :trailer-id="hoverTrailer" :title="hoverMedia.title || ''" :auto-preview="preview.hover.value.pointer" />
        <div class="title-hover-body">
          <div class="title-hover-heading"><h2>{{ hoverMedia.title }}</h2><span v-if="hoverRating" class="title-preview-rating">★ {{ hoverRating }}</span></div>
          <div class="title-preview-meta"><span v-if="hoverMedia.year">{{ hoverMedia.year }}</span><span>{{ preview.hover.value.mediaType === 'tv' ? 'TV series' : 'Movie' }}</span><span v-if="hoverMedia.source_label">{{ hoverMedia.source_label }}</span></div>
          <p v-if="hoverMedia.overview" class="title-hover-overview">{{ hoverMedia.overview }}</p>
          <p v-else class="title-hover-overview">Open this title for details and ways to watch.</p>
          <div class="title-hover-actions">
            <button type="button" class="dz-btn-primary" @click="openHovered"><AppIcon name="info" />More info</button>
            <RouterLink v-if="hoverTrailer" :to="{ name: 'youtube-play', params: { videoId: hoverTrailer } }" class="dz-btn-secondary"><AppIcon name="play" />Trailer</RouterLink>
          </div>
        </div>
      </aside>
    </Transition>

    <Transition :css="false" @enter="enter" @leave="leave" @enter-cancelled="cancelAnimation" @leave-cancelled="cancelAnimation">
      <TitleDetailsModal v-if="selection" :selection="selection" />
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, defineAsyncComponent, defineComponent, h, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/AppIcon.vue'
import TitlePreviewMedia from '@/components/TitlePreviewMedia.vue'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { useTitlePreview } from '@/composables/useTitlePreview'
import { useFocusTrap } from '@/composables/useFocusTrap'
import { canUseTitlePreviewRoute, parseTitlePreview, pickTrailerVideoId, placeTitlePreview, TITLE_PREVIEW_QUERY } from '@/helpers/title-preview'
import { formatRating } from '@/helpers/rating-badge'
import '@/assets/title-preview.css'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const ui = useUiStore()
const preview = useTitlePreview()
const selection = computed(() => auth.canBrowseTorrents && !ui.tvMode && canUseTitlePreviewRoute(route) ? parseTitlePreview(route.query[TITLE_PREVIEW_QUERY]) : null)
const hoverMedia = computed(() => ({ ...preview.hover.value?.item, ...preview.hover.value?.details }))
const hoverTrailer = computed(() => pickTrailerVideoId(hoverMedia.value.videos))
const hoverRating = computed(() => formatRating(hoverMedia.value.vote_average, hoverMedia.value.vote_count))
const hoverStyle = computed(() => {
  if (!preview.hover.value) return {}
  const position = placeTitlePreview(preview.hover.value.rect, window.innerWidth, window.innerHeight)
  return { width: `${position.width}px`, left: `${position.left}px`, top: `${position.top}px` }
})

const Pending = defineComponent({
  props: { error: { default: null } },
  setup(props) {
    const dialog = ref(null)
    useFocusTrap(dialog, { onClose: preview.closeTitle, returnFocus: false })
    return () => h('div', { class: 'title-detail-backdrop' }, [h('section', { ref: dialog, class: 'title-detail-panel title-detail-pending', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Title details' }, [
      h('h2', props.error ? 'This preview couldn’t open.' : 'Opening title…'),
      h('button', { class: 'dz-btn-secondary', onClick: preview.closeTitle }, 'Close'),
      selection.value && h(RouterLink, { to: `/browse/${selection.value.mediaType}/${selection.value.id}` }, { default: () => 'Open full details' }),
    ])])
  },
})
const loadModal = () => import('@/components/TitleDetailsModal.vue')
const TitleDetailsModal = defineAsyncComponent({ loader: loadModal, loadingComponent: Pending, errorComponent: Pending, delay: 0, timeout: 15000 })
watch(() => preview.hover.value?.key, key => { if (key) loadModal().catch(() => {}) })

function openHovered() {
  const hovered = preview.hover.value
  if (hovered) preview.openTitle(hoverMedia.value, hovered.target, hovered.owner)
}
function onHoverFocusOut(event) {
  if (!event.currentTarget.contains(event.relatedTarget) && !event.currentTarget.matches(':hover')) preview.leaveHover()
}
function onHoverMouseLeave(event) {
  if (!event.currentTarget.contains(document.activeElement)) preview.leaveHover()
}
function motionAllowed() { return !ui.tvMode && !window.matchMedia('(prefers-reduced-motion: reduce)').matches }
const animations = new WeakMap()
function cancelAnimation(el) { animations.get(el)?.forEach(animation => animation.cancel()) }
function animatePanel(el, opening, done) {
  const panel = el.querySelector('.title-detail-panel')
  if (!panel?.animate || !motionAllowed()) { done(); return }
  const rect = panel.getBoundingClientRect()
  const origin = preview.origin.value?.rect
  const dx = origin ? Math.max(-160, Math.min(160, (origin.left + origin.width / 2 - rect.left - rect.width / 2) * .3)) : 0
  const dy = origin ? Math.max(-100, Math.min(100, (origin.top + origin.height / 2 - rect.top - rect.height / 2) * .3)) : 18
  const small = { opacity: 0, transform: `translate(${dx}px, ${dy}px) scale(${opening ? '.78' : '.92'})` }
  const full = { opacity: 1, transform: 'translate(0, 0) scale(1)' }
  const duration = opening ? 380 : 180
  const panelAnimation = panel.animate(opening ? [small, full] : [full, small], { duration, easing: 'cubic-bezier(.16,1,.3,1)' })
  const backdropAnimation = el.animate(opening ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }], { duration: opening ? 220 : duration })
  animations.set(el, [panelAnimation, backdropAnimation])
  panelAnimation.finished.catch(() => {}).then(done)
}
function enter(el, done) { animatePanel(el, true, done) }
function leave(el, done) { animatePanel(el, false, done) }
function hideHover() { preview.hideHover() }
function onKey(event) {
  if (event.key !== 'Escape' || selection.value || !preview.hover.value) return
  event.preventDefault()
  event.stopPropagation()
  hideHover()
}
function onVisibility() { if (document.hidden) hideHover() }
watch(() => route.fullPath, hideHover)
watch(() => auth.canBrowseTorrents && !ui.tvMode, allowed => { if (!allowed) hideHover() })
watch(() => [auth.status, auth.deviceKey], ([status, key], [previousStatus, previousKey]) => {
  if (!['admin', 'unlocked'].includes(previousStatus) || (status === previousStatus && key === previousKey)) return
  hideHover()
  preview.seed.value = null
  preview.origin.value = null
  if (route.query[TITLE_PREVIEW_QUERY] != null) {
    const query = { ...route.query }
    delete query[TITLE_PREVIEW_QUERY]
    router.replace({ path: route.path, query, hash: route.hash }).catch(() => {})
  }
})
onMounted(() => {
  window.addEventListener('scroll', hideHover, true)
  window.addEventListener('resize', hideHover)
  window.addEventListener('blur', hideHover)
  document.addEventListener('visibilitychange', onVisibility)
  document.addEventListener('keydown', onKey, true)
})
onUnmounted(() => {
  hideHover()
  window.removeEventListener('scroll', hideHover, true)
  window.removeEventListener('resize', hideHover)
  window.removeEventListener('blur', hideHover)
  document.removeEventListener('visibilitychange', onVisibility)
  document.removeEventListener('keydown', onKey, true)
})
</script>
